'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

/**
 * A back door for showing credits and usage to a specific person — a demo, a
 * customer call, a production smoke test — without turning
 * `usage-based-billing-on-dashboard` on for a cohort. Append the code once:
 *
 *   https://dash.blinq.me/<workspace>/home?billing_secret_test_code=4bankplace
 *
 * It then sticks for the rest of the browser tab, so the tester can navigate
 * normally. A new tab starts closed again.
 *
 * This hides the surface; it does not protect it. The code ships in the client
 * bundle and anyone can read it there. What actually guards the data is the
 * workspace ADMIN check on `workspaceCreditSummary`, which this cannot bypass,
 * and the role gate on the row itself.
 */
const SECRET_PARAM = 'billing_secret_test_code';
const SECRET_CODE = '4bankplace';
const STORAGE_KEY = 'blinq-credit-usage-secret-access';

/** Reads the flag-independent unlock: this page's code, or one used earlier in the tab. */
export function useCreditUsageSecretAccess(): boolean {
  const searchParams = useSearchParams();
  const hasCode = searchParams?.get(SECRET_PARAM) === SECRET_CODE;
  const [unlockedEarlier, setUnlockedEarlier] = useState(false);

  useEffect(() => {
    // Storage throws in some privacy modes; a back door is never worth an
    // exception on a page every authenticated user loads.
    try {
      if (hasCode) {
        window.sessionStorage.setItem(STORAGE_KEY, 'true');
        return;
      }

      setUnlockedEarlier(window.sessionStorage.getItem(STORAGE_KEY) === 'true');
    } catch {
      setUnlockedEarlier(false);
    }
  }, [hasCode]);

  return hasCode || unlockedEarlier;
}
